import { useEffect, useState } from "react";
import { Trophy, Smartphone, Check, Share2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/i18n";
import { qrDataUrl, verifyUrl } from "@/lib/giftcard";

// Shown to the #1 player on the board: their $25 gift-card coupon with a QR the
// restaurant scans (opens nicabros.com/?verify=CODE → live valid/redeemed/expired status).
export function WinnerCard({ award, challenge }) {
  const { lang } = useLang();
  const es = lang === "es";
  const [qr, setQr] = useState("");
  const [saved, setSaved] = useState(false);
  const c = award?.coupon;
  useEffect(() => { if (c?.code) qrDataUrl(verifyUrl(c.code)).then(setQr).catch(() => {}); }, [c?.code]);
  if (!c) return null;
  const now = new Date().toISOString();
  const status = c.redeemed ? "redeemed" : ((c.expires_at || "") <= now ? "expired" : "valid");
  const exp = c.expires_at ? new Date(c.expires_at).toLocaleDateString(es ? "es-NI" : "en-US", { day: "numeric", month: "short", year: "numeric" }) : "";
  const save = () => {
    if (!qr) return;
    const a = document.createElement("a"); a.href = qr; a.download = `${c.code}.png`; document.body.appendChild(a); a.click(); a.remove();
    setSaved(true);
  };
  return (
    <div className="mt-3 rounded-xl bg-white text-[#0a1c38] p-3 text-center" data-testid="giftcard-winner-card">
      <p className="font-pixel text-[9px] text-[#c28800] flex items-center justify-center gap-1.5"><Trophy size={12} strokeWidth={2.5} /> {es ? "¡GANASTE LA GIFT CARD!" : "YOU WON THE GIFT CARD!"}</p>
      <p className="font-vt text-lg leading-tight mt-1">${award.amount || 25} · {c.brand}</p>
      {qr && <img src={qr} alt="QR" className="w-36 h-36 mx-auto mt-2 rounded-lg" data-testid="giftcard-winner-qr" />}
      <p className="font-pixel text-[10px] tracking-wider mt-2 break-all" data-testid="giftcard-winner-code">{c.code}</p>
      <p className={`font-pixel text-[8px] mt-1 ${status === "valid" ? "text-[#2e7d32]" : "text-[#999]"}`} data-testid="giftcard-winner-status">
        {status === "valid" ? (es ? `VÁLIDO · vence ${exp}` : `VALID · expires ${exp}`) : status === "redeemed" ? (es ? "YA CANJEADO" : "REDEEMED") : (es ? "VENCIDO" : "EXPIRED")}
      </p>
      <p className="font-vt text-sm text-[#0a1c38]/70 mt-1 leading-tight">{es ? "Mostrá este QR en el restaurante; al escanearlo ven que tu gift card es válida." : "Show this QR at the restaurant; scanning it confirms your gift card is valid."}</p>
      <button data-testid="giftcard-winner-save" onClick={save} className="mt-2 w-full pixel-btn bg-[#0a1c38] text-white px-3 py-2 text-[9px] flex items-center justify-center gap-1.5">
        {saved ? <Check size={13} strokeWidth={3} /> : <Smartphone size={13} strokeWidth={2.5} />} {saved ? (es ? "Guardado" : "Saved") : (es ? "Guardar QR en el teléfono" : "Save QR to phone")}
      </button>
    </div>
  );
}

// Compact "share my run" button (WhatsApp share sheet with image; download fallback).
export function ShareRunButton({ onShare, className = "" }) {
  const { lang } = useLang();
  const es = lang === "es";
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const r = await onShare();
      if (r === "downloaded") toast.success(es ? "Imagen guardada y texto copiado — ¡pegalo en WhatsApp!" : "Image saved & text copied — paste it in WhatsApp!");
      else if (r === "failed") toast.error(es ? "No se pudo crear la imagen" : "Could not build the image");
    } finally { setBusy(false); }
  };
  return (
    <button data-testid="giftcard-share-run-btn" onClick={go} disabled={busy} className={`pixel-btn bg-[#25D366] text-black px-3 py-2.5 text-[10px] flex items-center justify-center gap-1.5 disabled:opacity-70 ${className}`}>
      <Share2 size={13} strokeWidth={2.5} /> {busy ? "…" : (es ? "Compartir mi corrida" : "Share my run")}
    </button>
  );
}
